<!--
📌 Componente: VitrineHeaderNotification.vue
📂 Caminho: src/components/VitrineHeaderNotification.vue
📝 Descrição: Barra de aviso fixa no topo da vitrine, posicionada antes do
  header principal.

👁️ Interface e Interação com o Usuário:
  - Mensagem traduzida via i18n.
  - Botão permite fechar manualmente a notificação.
  - Altura emitida para ajustar o layout do restante da página.

📥 Entrada (Props):
  - Nenhuma.

📤 Saída (Emits):
  - `@height-change` - Emite a altura atual da barra (px) para ajustar o
    layout.

🔗 Dependências:
  - **Utilitários JS:** Nenhum.
  - **Componentes Vue:** Nenhum.
  - **Backend (Cloud Functions/API):** Nenhum.
  - **Bibliotecas externas:** vue, vue-i18n.
-->

<template>
  <Transition
    name="vh-notification"
    @after-enter="emitHeight"
    @after-leave="emitHeight"
  >
    <div
      v-if="isVisible"
      ref="barEl"
      :class="[
        'fixed left-0 right-0 top-0 z-30',
        'bg-vermelho-suave text-branco',
        'pt-[env(safe-area-inset-top)] px-4 py-3 shadow-md',
      ]"
      role="status"
      aria-live="polite"
    >
      <div
        class="container mx-auto flex items-center justify-between gap-4 px-4 sm:px-6 md:px-12"
      >
        <!-- Espaçador invisível para balancear o botão de fechar e manter o texto centralizado -->
        <div class="h-8 w-8 flex-none hidden sm:block" aria-hidden="true"></div>

        <p
          class="m-0 flex-1 text-center font-semibold text-sm md:text-lg lg:text-xl leading-tight"
        >
          {{ t("vitrine.headerNotification.disclaimer") }}
        </p>

        <button
          type="button"
          :class="[
            'flex h-8 w-8 flex-none items-center justify-center rounded-full',
            'text-branco transition hover:bg-branco/10',
            'focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-branco/70',
          ]"
          :aria-label="t('vitrine.common.close')"
          @click="closeBar"
        >
          <span class="sr-only">{{ t("vitrine.common.close") }}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, nextTick, watch, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["height-change"]);

const { t } = useI18n();
const isVisible = ref(true);
const barEl = ref(null);
let autoHideTimer = 0;

const closeBar = () => {
  window.clearTimeout(autoHideTimer);
  autoHideTimer = 0;
  isVisible.value = false;
};

const measureHeight = () => {
  const el = barEl.value;
  return el ? Math.ceil(el.getBoundingClientRect().height) : 0;
};

const emitHeight = () => {
  emit("height-change", measureHeight());
};

const scheduleAutoHide = () => {
  window.clearTimeout(autoHideTimer);
  if (!isVisible.value) return;
  autoHideTimer = window.setTimeout(() => {
    isVisible.value = false;
    autoHideTimer = 0;
  }, 10000);
};

onMounted(async () => {
  await nextTick();
  emitHeight();
  scheduleAutoHide();
});

watch(isVisible, async () => {
  await nextTick();
  emitHeight();
  if (isVisible.value) {
    scheduleAutoHide();
  } else {
    window.clearTimeout(autoHideTimer);
    autoHideTimer = 0;
  }
});

onBeforeUnmount(() => {
  window.clearTimeout(autoHideTimer);
  autoHideTimer = 0;
});
</script>

<style scoped>
.vh-notification-enter-active,
.vh-notification-leave-active {
  transition: max-height 0.25s ease, opacity 0.25s ease;
}

.vh-notification-enter-from,
.vh-notification-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
